import {isSafeLogoUrl} from './companyLogo'

const escapeAttr = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

// What goes in the card's bottom-left footer cell: the account's own logo when
// it has one, otherwise the plain company name (the current, unchanged
// behavior). Every builder below just interpolates the result, so this is the
// single place that knows about branding.
//
// The logo is bounded so a wide or tall image can't enlarge the fixed 260px card
// or crowd out the "Powered by TrueDesking" cell beside it. The uploaded logo is
// stored at high resolution (up to 600x200) for crispness, so the display size is
// enforced here instead of by the stored pixels. Outlook desktop ignores CSS
// max-width/max-height, so an explicit `height="16"` attribute is set for it (the
// crop aspect never exceeds 3:1, so at 16px tall the width stays within the 110px
// cap and aspect is preserved). CSS-aware clients use the style bounds. The
// company name stays on as alt text so image-blocking clients show it instead of
// a broken-image placeholder.
const LOGO_DISPLAY_HEIGHT = 16
const buildCompanyBranding = (companyName, companyLogoUrl) => (
    isSafeLogoUrl(companyLogoUrl)
        ? `<img src="${companyLogoUrl}" alt="${escapeAttr(companyName)}" height="${LOGO_DISPLAY_HEIGHT}" style="max-height: ${LOGO_DISPLAY_HEIGHT}px; max-width: 110px; height: auto; width: auto; vertical-align: middle;">`
        : companyName
)

export const copyQuoteLeaseHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>
      </tr>
      
      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>
      
      <!-- Total Drive Off -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Total Drive Off:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.amount_due_at_signing.toDollarFormat()}</td>
      </tr>
      
      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>
      
      <!-- Term -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Term:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.term}</td>
      </tr>
      
      <!-- Mileage -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Mileage:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.mileage}/year</td>
      </tr>
      
      <!-- Residual -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Residual:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">
            ${dealState.formatted_quote.residual}%, ${dealState.formatted_quote.residual_value.toDollarFormat()}
        </td>
      </tr>
      
      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

// In-app tooltip shown on the DeskingPage quote grid. Unlike the copy-to-clipboard
// summaries above (which are always a white card meant for pasting into emails),
// this one renders inside the app and so follows the app theme. Pass isDark to get
// the dark-mode palette; the colors mirror the DeskingPage dark tokens.
export const copyQuoteLeaseSummaryHTMLTooltip = (quote, isDark = false, isPurchase = false) => {
    const DASH = '—'
    const isBlank = (v) => v === null || v === undefined || v === ''
    const money = (v) => isBlank(v) ? DASH : v.toDollarFormat()

    const driveOff = money(quote.amount_due_at_signing ?? quote.drive_off)
    // Finance deals quote an APR; leases show either a rate or a money factor.
    // Rate banks (e.g. Ally, Ford Financial) carry a display-ready rate string
    // ("7.05%") on quote.rate and money-factor banks carry null — the same
    // backend signal DealPagePayment keys on for its Rate/Money Factor split.
    const hasRate = !isPurchase && !isBlank(quote.rate)
    const rateLabel = isPurchase ? 'APR:' : (hasRate ? 'Rate:' : 'Money Factor:')
    const rateValue = isPurchase
        ? (isBlank(quote.apr) ? DASH : `${quote.apr}%`)
        : hasRate
            ? quote.rate
            : (isBlank(quote.money_factor) ? DASH : quote.money_factor)
    const residualRaw = quote.residual ?? quote.residual_percentage
    const residual = isBlank(residualRaw) ? DASH : `${residualRaw}%`

    const programDescription = isBlank(quote.program_description)
        ? (quote.lender_name || DASH)
        : quote.program_description

    const c = isDark
        ? { cardBg: '#1e2435', border: '#252d3d', label: '#8b95a8', value: '#f0f2f5', shadow: '0 4px 16px rgba(0, 0, 0, 0.5)' }
        : { cardBg: '#ffffff', border: '#e5e7eb', label: '#4b5563', value: '#111827', shadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: ${c.shadow}; border: 1px solid ${c.border};">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: ${c.cardBg}; font-family: Arial, sans-serif; border: 1px solid ${c.border}; border-radius: 8px; overflow: hidden;">
      <!-- Program description (title / header) -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid ${c.border}; color: ${c.value}; font-size: 12px; font-weight: 700; line-height: 1.35;">${programDescription}</td>
      </tr>

      ${isPurchase ? '' : `
      <!-- Drive Off (lease only) -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid ${c.border}; color: ${c.label}; font-size: 11px;">Drive Off:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid ${c.border}; color: ${c.value}; font-size: 11px; font-weight: 600; text-align: right;">${driveOff}</td>
      </tr>`}

      <!-- Money Factor (lease) / APR (finance). On finance this is the last row,
           so it drops its bottom border (the Residual row below is lease-only). -->
      <tr>
        <td style="padding: 6px 12px; ${isPurchase ? '' : `border-bottom: 1px solid ${c.border};`} color: ${c.label}; font-size: 11px;">${rateLabel}</td>
        <td style="padding: 6px 12px; ${isPurchase ? '' : `border-bottom: 1px solid ${c.border};`} color: ${c.value}; font-size: 11px; font-weight: 600; text-align: right;">${rateValue}</td>
      </tr>
      ${isPurchase ? '' : `
      <!-- Residual Percentage (lease only) -->
      <tr>
        <td style="padding: 6px 12px; color: ${c.label}; font-size: 11px;">Residual Percentage:</td>
        <td style="padding: 6px 12px; color: ${c.value}; font-size: 11px; font-weight: 600; text-align: right;">${residual}</td>
      </tr>`}
    </table>
    </div>
        `
    )
}

export const copyQuoteFinanceHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>
      </tr>
      
      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>
      
      <!-- Total Drive Off -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Down Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.amount_due_at_signing.toDollarFormat()}</td>
      </tr>
      
      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>
      
      <!-- Term -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Term:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.term}</td>
      </tr>
      
      <!-- APR -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">APR:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.apr}%</td>
      </tr>
      
      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

export const copyQuoteDealerFinanceHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    const firstPaymentWaiverAmount = dealState?.formatted_quote.first_payment_waived_amount || 0
    const rebates = dealState.formatted_quote.rebates.filter(r => r.amount !== '0')

    const totalRebatesAmount = rebates.reduce(function(accumulator, rebate) {
        if (rebate.is_applied) {
            return accumulator + Number(rebate.amount);
        } else {
            return accumulator
        }
    }, 0) + Number(firstPaymentWaiverAmount)

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>     
      </tr>
      
      <!-- Lender -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong>${dealState.formatted_quote.lender_name}</strong>
        </td>
      </tr>
      
      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>
      
      <!-- Selling Price -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Selling Price:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.selling_price.toDollarFormat()}</td>
      </tr>
      
      <!-- Total Drive Off -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Down Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.amount_due_at_signing.toDollarFormat()}</td>
      </tr>
      
      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>
      
      <!-- Term -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Term:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.term}</td>
      </tr>  

      <!-- Total Rebates -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Total Rebates:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${totalRebatesAmount.toDollarFormat()}</td>
      </tr>       
      
      <!-- Broker Fee -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Broker Fee:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.profit.toDollarFormat()}</td>
      </tr>
      
      <!-- APR -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">APR:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.apr}%</td>
      </tr>
      
      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

export const copyQuoteDealerLeaseHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    // Rate banks (e.g. Ally, Ford Financial) carry a display-ready rate string
    // ("7.05%") on formatted_quote.rate and money-factor banks carry null — the
    // same backend signal DealPagePayment keys on for its Rate/Money Factor split.
    const hasRate = !!dealState.formatted_quote.rate

    const firstPaymentWaiverAmount = dealState?.formatted_quote.first_payment_waived_amount || 0
    const rebates = dealState.formatted_quote.rebates.filter(r => r.amount !== '0')
    const totalRebatesAmount = rebates.reduce(function(accumulator, rebate) {
        if (rebate.is_applied) {
            return accumulator + Number(rebate.amount);
        } else {
            return accumulator
        }
    }, 0) + Number(firstPaymentWaiverAmount)

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>
      </tr>
      
      <!-- Lender -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong>${dealState.formatted_quote.lender_name}</strong>
        </td>
      </tr>
      
      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>
      
      <!-- Selling Price -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Selling Price:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.selling_price.toDollarFormat()}</td>
      </tr>
      
      <!-- Total Drive Off -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Total Drive Off:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.amount_due_at_signing.toDollarFormat()}</td>
      </tr>
      
      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>
      
      <!-- Term -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Term:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.term}</td>
      </tr>      
      
      <!-- Mileage -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Mileage:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.mileage}</td>
      </tr>  
       
      <!-- Total Rebates -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Total Rebates:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${totalRebatesAmount.toDollarFormat()}</td>
      </tr>       
      
      <!-- Broker Fee -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Broker Fee:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.profit.toDollarFormat()}</td>
      </tr>
      
      <!-- Rate (rate banks) / Money Factor -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">${hasRate ? 'Rate:' : 'Money Factor:'}</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${hasRate ? dealState.formatted_quote.rate : dealState.formatted_quote.money_factor}</td>
      </tr>
      
      <!-- Residual -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Residual:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">
            ${dealState.formatted_quote.residual}%, ${dealState.formatted_quote.residual_value.toDollarFormat()}
        </td>
      </tr>
      
      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

// Cash (one-pay purchase) has no amortized Payment/APR/Term — its Payment panel
// is a flat Selling Price + Taxes + Fees breakdown. These builders mirror the
// Finance card structure/styling but render the cash-relevant rows only.
export const copyQuoteCashHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    const sumAmounts = (arr) => (arr || []).reduce((acc, x) => acc + (Number(x.amount) || 0), 0)
    const taxesTotal = sumAmounts(dealState.formatted_quote.taxes)
    const feesTotal = sumAmounts(dealState.formatted_quote.fees)

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>
      </tr>

      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>

      <!-- Selling Price -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Selling Price:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.selling_price.toDollarFormat()}</td>
      </tr>

      <!-- Taxes -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Taxes:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${taxesTotal.toDollarFormat()}</td>
      </tr>

      <!-- Fees -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Registration and Fees:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${feesTotal.toDollarFormat()}</td>
      </tr>

      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>

      <!-- Tax Rate -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Tax Rate:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.tax_rate}%</td>
      </tr>

      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

export const copyQuoteDealerCashHTMLTooltip = (dealState, companyBranding) => {
    const car_detail = dealState.car_detail

    const firstPaymentWaiverAmount = dealState?.formatted_quote.first_payment_waived_amount || 0
    const rebates = dealState.formatted_quote.rebates.filter(r => r.amount !== '0')
    const totalRebatesAmount = rebates.reduce(function(accumulator, rebate) {
        if (rebate.is_applied) {
            return accumulator + Number(rebate.amount);
        } else {
            return accumulator
        }
    }, 0) + Number(firstPaymentWaiverAmount)

    return (
        `
    <div style="width: 260px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: 1px solid #e5e7eb;">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 260px; background-color: #ffffff; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
      <!-- Car Name -->
      <tr>
        <td colspan="2" style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">
          <strong style="color: #111827;">${car_detail.year} ${car_detail.make} ${car_detail.model}</strong><br>
          ${car_detail.trim}
        </td>
      </tr>

      <!-- MSRP -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">MSRP:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.msrp.toDollarFormat()}</td>
      </tr>

      <!-- Selling Price -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Selling Price:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.selling_price.toDollarFormat()}</td>
      </tr>

      <!-- Payment -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Payment:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.formatted_quote.monthly_payment.toDollarFormat()}</td>
      </tr>

      <!-- Total Rebates -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Total Rebates:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${totalRebatesAmount.toDollarFormat()}</td>
      </tr>

      <!-- Broker Fee -->
      <tr>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #4b5563; font-size: 11px;">Broker Fee:</td>
        <td style="padding: 6px 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 11px; font-weight: 600; text-align: right;">${dealState.profit.toDollarFormat()}</td>
      </tr>

      <!-- Footer -->
      <tr>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; color: #111827; font-size: 12px; font-weight: 700;">
          ${companyBranding}
        </td>
        <td style="background-color: #f9fafb; border-top: 1px solid #e5e7eb; padding: 8px 12px; text-align: right; color: #6b7280; font-size: 9px;">
          Powered by: <img src="https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png" alt="TrueDesking Logo" style="height: 14px; vertical-align: middle; padding: 2px 8px; border-radius: 3px;">
        </td>
      </tr>
    </table>
    </div>
    <p><br></p>
        `
    )
}

export const copyQuoteHTMLTooltip = (dealState, companyName, isDealer, companyLogoUrl) => {
    // Resolved once here so the builders stay branding-agnostic.
    const branding = buildCompanyBranding(companyName, companyLogoUrl)

    // Cash (one-pay purchase) has its own builders; must be checked before the
    // generic is_purchase (Finance) fall-through since cash is also is_purchase.
    const isCash = dealState.is_purchase && dealState.is_one_pay
    if (isCash) {
        return isDealer ? copyQuoteDealerCashHTMLTooltip(dealState, branding) : copyQuoteCashHTMLTooltip(dealState, branding)
    }
    if (!isDealer) {
        return dealState.is_purchase ? copyQuoteFinanceHTMLTooltip(dealState, branding) : copyQuoteLeaseHTMLTooltip(dealState, branding)
    } else {
        return dealState.is_purchase ? copyQuoteDealerFinanceHTMLTooltip(dealState, branding) : copyQuoteDealerLeaseHTMLTooltip(dealState, branding)
    }

}

const getSpaces = (word) => {
    const totalWhiteSpace = 20
    const wordLength = word.length
    return word + " ".repeat(Math.max(0, totalWhiteSpace - wordLength))
}

export const buildMiniQuoteSummaryString = (dealState, isDealer) => {
    const isPurchase = dealState.is_purchase
    const isCash = dealState.is_purchase && dealState.is_one_pay

    const firstPaymentWaiverAmount = dealState?.formatted_quote.first_payment_waived_amount || 0
    const rebates = dealState.formatted_quote.rebates.filter(r => r.amount !== '0')

    const totalRebatesAmount = rebates.reduce(function(accumulator, rebate) {
        if (rebate.is_applied) {
            return accumulator + Number(rebate.amount);
        } else {
            return accumulator
        }
    }, 0) + Number(firstPaymentWaiverAmount)

    const car_detail = dealState.car_detail

    // Cash (one-pay purchase) has its own line set — mirror the cash HTML builders
    // so both clipboard flavors match. Must be checked before the Finance path.
    if (isCash) {
        const sumAmounts = (arr) => (arr || []).reduce((acc, x) => acc + (Number(x.amount) || 0), 0)
        const taxesTotal = sumAmounts(dealState.formatted_quote.taxes)
        const feesTotal = sumAmounts(dealState.formatted_quote.fees)

        let cashString = ''
        cashString += `${car_detail.year} ${car_detail.make} ${car_detail.model}`
        cashString += "\n"
        cashString += `${car_detail.trim}`
        cashString += "\n"
        cashString += `${getSpaces("MSRP:")} ${dealState.msrp.toDollarFormat()}`
        cashString += "\n"
        cashString += `${getSpaces("Selling Price:")} ${dealState.selling_price.toDollarFormat()}`
        cashString += "\n"
        if (isDealer) {
            cashString += `${getSpaces("Payment:")} ${dealState.formatted_quote.monthly_payment.toDollarFormat()}`
            cashString += "\n"
            cashString += `${getSpaces("Total Rebates:")} ${totalRebatesAmount.toDollarFormat()}`
            cashString += "\n"
            cashString += `${getSpaces("Broker Fee:")} ${dealState.profit.toDollarFormat()}`
        } else {
            cashString += `${getSpaces("Taxes:")} ${taxesTotal.toDollarFormat()}`
            cashString += "\n"
            cashString += `${getSpaces("Registration and Fees:")} ${feesTotal.toDollarFormat()}`
            cashString += "\n"
            cashString += `${getSpaces("Payment:")} ${dealState.formatted_quote.monthly_payment.toDollarFormat()}`
            cashString += "\n"
            cashString += `${getSpaces("Tax Rate:")} ${dealState.formatted_quote.tax_rate}%`
        }
        return cashString
    }

    const driveOffText = isPurchase ? "Down Payment:" : "Total Drive Off:"

    let string = ''
    string += `${car_detail.year} ${car_detail.make} ${car_detail.model}`
    string += "\n"
    string += `${car_detail.trim}`
    string += "\n"
    if (isDealer) {
        string += `${getSpaces("Lender:")} ${dealState.formatted_quote.lender_name}`
        string += "\n"
    }
    string += `${getSpaces("MSRP:")} ${dealState.msrp.toDollarFormat()}`
    string += "\n"
    if (isDealer) {
        string += `${getSpaces("Selling Price:")} ${dealState.selling_price.toDollarFormat()}`
        string += "\n"
    }
    string += `${getSpaces(driveOffText)} ${dealState.formatted_quote.amount_due_at_signing.toDollarFormat()}`
    string += "\n"
    string += `${getSpaces("Payment:")} ${dealState.formatted_quote.monthly_payment.toDollarFormat()}`
    string += "\n"
    string += `${getSpaces("Term:")} ${dealState.term}`
    string += "\n"
    if (isDealer) {
        string += `${getSpaces("Total Rebates:")} ${totalRebatesAmount.toDollarFormat()}`
        string += "\n"
        string += `${getSpaces("Broker Fee:")} ${dealState.profit.toDollarFormat()}`
        string += "\n"
        // Rate banks carry a display-ready rate string on formatted_quote.rate
        // (null for money-factor banks) — same split as the dealer lease HTML card.
        const hasRate = !!dealState.formatted_quote.rate
        string += hasRate
            ? `${getSpaces("Rate:")} ${dealState.formatted_quote.rate}`
            : `${getSpaces("Money Factor:")} ${dealState.formatted_quote.money_factor}`
        string += "\n"
    }
    if (isPurchase) {
        string += `${getSpaces("APR:")} ${dealState.formatted_quote.apr}%`
    } else {
        string += `${getSpaces("Mileage:")} ${dealState.mileage}/year`
        string += "\n"
        string += `${getSpaces("Residual:")} ${dealState.formatted_quote.residual}%, ${dealState.formatted_quote.residual_value.toDollarFormat()}`
    }
    return string
}

// Add for Option Codes
// ${car_detail.option_codes.map(oc => `<span>${oc.name}</span>`).join('')}
